/**
 * Lightweight test runtime for sandbox code.
 *
 * Provides describe/it/expect/beforeEach/afterEach/beforeAll/afterAll that
 * test files can import via `@gotti/sandbox-test`. Works within compileAndLoad's
 * virtual module system — both in the browser sandbox and the AI backend
 * RunTestsTool.
 *
 * Usage in test files:
 *   import { describe, it, expect, beforeEach, beforeAll } from '@gotti/sandbox-test';
 *
 * The runtime collects test registrations synchronously during module load,
 * then `runCollectedTests()` executes them and returns structured results.
 *
 * Features:
 *   - Nested describe() — inner suites inherit parent beforeEach/afterEach
 *   - beforeAll / afterAll — suite-level setup/teardown (runs once per suite)
 *   - Per-test timeout — prevents a hanging await from freezing the runner
 *   - Progress callbacks — real-time streaming of test state changes
 *
 * ── Parity tightenings (server-side-test-runner-with-parity-design 2026-05-30) ──
 *   - Result objects strip `.stack`. `TestResult.error` is now
 *     `{ name, message }` only — the raw stack format diverges between Node
 *     and jsdom-in-Node and would break byte-identical parity.
 *   - `TestResult` / `SuiteResult` carry no `durationMs`. Durations live in
 *     a separate `RunMetadata` that the coordinator does NOT compare.
 *   - `installDeterministicGlobals(seed)` / `restoreGlobals()` install a
 *     seeded `Math.random` (mulberry32), frozen `Date.now`, and matching
 *     `performance.now`. Both runners install before `runTests` and restore
 *     after, so any spec that needs RNG or wall-clock gets identical bytes
 *     from both environments.
 *   - `NonDeterministicCallError` is exported as the named class the shim
 *     would surface if a spec somehow bypassed it. The shim itself is the
 *     enforcement (a global override), so no per-call runtime check exists
 *     beyond that.
 *   - `TestResult.file` / `SuiteResult.file` use the verbatim string the
 *     engine received via `setCurrentTestFile(path)`. The engine does NOT
 *     resolve or normalize the path — adapters are responsible for
 *     supplying the exact key they want surfaced.
 */

// ─────────────────────────────────────────────────────────────────────────────
import { getRealSetTimeout, getRealClearTimeout, setPerformanceNow } from './virtualClock';

// Types
// ─────────────────────────────────────────────────────────────────────────────

export type TestStatus = 'passed' | 'failed' | 'error' | 'skipped' | 'running' | 'pending';

/**
 * Error info surfaced on a TestResult. Intentionally narrow: `name` + `message`
 * plus an optional `errorFrames` array of virtual-path call-sites parsed from
 * the thrown error's V8 `.stack`. The raw `.stack` string is NEVER surfaced
 * — its format diverges between Node (V8 native) and jsdom-in-Node and would
 * break byte-identical parity.
 *
 * `errorFrames` IS safe for parity because:
 *   - `@gotti/sandbox-core`'s transpiler appends `//# sourceURL=${entryFile}`
 *     so V8 stack frames carry the same virtual path in both envs.
 *   - The parser keeps only frames whose path starts with `/` (= virtual
 *     files we know about via the sourceURL convention), dropping
 *     `<anonymous>`, `node_modules`, jsdom internals, etc.
 *   - The shape is normalized to the literal string
 *     `/virtual/path/to/file.ts:LINE:COL` — identical across V8 envs.
 *   - Capped at 5 frames for compact, deterministic payloads.
 */
export interface TestErrorInfo {
  name: string;
  message: string;
  errorFrames?: string[];
}

/**
 * One distinct console call made by the GENERATED system code while a test ran,
 * with its source call-site (from the V8 stack via the `//# sourceURL` frames)
 * and how many times it fired. Surfaced to the fix planner so it can see what
 * the code actually DID during the failing test, not just the assertion that
 * failed. Test-spec / framework logs are filtered out — EXCEPT the framework's
 * own "I caught a throw from your system" reports (see
 * `isCaughtSystemErrorLog`), which are deliberately let through: they name the
 * exact undefined method / thrown error inside generated code, and are
 * otherwise invisible (the framework logs+swallows or logs+rethrows them from
 * its own real, non-virtual files, which never resolve to an attributable
 * call-site). Those entries get `site: '<framework>'`.
 */
export interface CapturedLog {
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  /** Virtual path:line:col of the call-site, e.g. `/server/.../SnakeServerSystem.ts:42:7`. */
  site: string;
  message: string;
  count: number;
}

export interface TestResult {
  name: string;
  suiteName: string;
  status: TestStatus;
  /** Verbatim file string the engine received via setCurrentTestFile. */
  file?: string;
  error?: TestErrorInfo;
  /** Distinct system-code console calls made during this test (deduped+counted). */
  logs?: CapturedLog[];
}

export interface SuiteResult {
  name: string;
  /** Verbatim file string the engine received via setCurrentTestFile. */
  file?: string;
  tests: TestResult[];
  passed: number;
  failed: number;
  skipped: number;
}

/**
 * Trimmed per-file line-coverage summary threaded through
 * `TestRunResult.coverage`. Hand-mirrors `@gotti/sandbox-ai-v2`'s
 * `CoverageSummary` (this package has no dependency on sandbox-ai-v2, so the
 * shape is hand-mirrored rather than imported) — percentage + per-file
 * percentage AND the 1-based uncovered (`hits === 0`) line numbers (ascending,
 * capped at 200 per file) so the deep agent's `get_coverage` tool can show
 * the EXACT untested lines, not just an aggregate percentage. Deliberately
 * NEVER the full per-line hit counters (`@gotti/sandbox-core`'s
 * `CoverageReport`/`FileCoverage`) — those stay local to the producing
 * runner; this is the bounded WIRE shape both `NodeRunnerAdapter` and the
 * browser-relay path (`apps/sandbox`'s `AgentContext.tsx`) populate before it
 * reaches the deep agent gate (`deepAgent/graph.ts`).
 */
export interface CoverageSummary {
  percentage: number;
  files: Array<{
    path: string;
    percentage: number;
    uncoveredLines: number[];
  }>;
}

export interface TestRunResult {
  suites: SuiteResult[];
  totalTests: number;
  totalPassed: number;
  totalFailed: number;
  totalSkipped: number;
  /**
   * Line-coverage summary. Populated by `NodeRunnerAdapter` (converted from
   * `CoverageTracker.collect()`'s full `CoverageReport`, dropping per-line hit
   * arrays but keeping `uncoveredLines`); the in-package `BrowserRunnerAdapter`
   * still never collects it (coverage is a UI concern owned by
   * `SandboxRunnerProvider`, not this adapter). The PRODUCTION browser-relay
   * runner (`apps/sandbox-backend`'s `runnerRelay.ts`) DOES forward this same
   * trimmed shape so the deep agent's coverage gate enforces in browser mode
   * too — see `v2ResultToEngineResult`. ADDITIVE metadata either way: pass/fail
   * counts are the only fields runners must agree on, so `DualRunner.run()`
   * and the parity meta-suite's `runParity()` strip this field before their
   * `JSON.stringify` byte-comparison — it never triggers a
   * `ParityDivergenceError`.
   */
  coverage?: CoverageSummary;
  /** Set when the run's `signal` aborted before every test ran: the result holds the tests that did. Absent otherwise. */
  cancelled?: true;
}

/**
 * Wall-clock durations for the run, returned alongside but NOT inside
 * `TestRunResult`. The coordinator compares `TestRunResult` JSON byte-for-byte;
 * durations are intrinsically non-deterministic so they live here, never on
 * the compared payload.
 */
export interface RunMetadata {
  durationMs: number;
  perTestDurations: Array<{
    suiteName: string;
    testName: string;
    durationMs: number;
  }>;
}

/** Callback fired as each test/suite transitions through states. */
export type TestProgressEvent =
  | { type: 'run-start'; totalSuites: number; totalTests: number }
  | { type: 'suite-start'; suiteName: string; file?: string; testCount: number }
  | { type: 'test-start'; suiteName: string; testName: string; file?: string }
  | { type: 'test-end'; suiteName: string; testName: string; file?: string; status: TestStatus; error?: TestErrorInfo }
  | { type: 'suite-end'; suite: SuiteResult }
  | { type: 'run-end'; result: TestRunResult };

export type OnTestProgress = (event: TestProgressEvent) => void;

export interface RunTestsOptions {
  /** Per-test timeout in milliseconds. Default: 10 000 (10s). */
  testTimeoutMs?: number;
  /** Overall run timeout in milliseconds. Default: 60 000 (60s). 0 = no limit. */
  runTimeoutMs?: number;
  /** Progress callback — fired for every state transition. */
  onProgress?: OnTestProgress;
  /** Run only this test, with its suite's hooks; no other suite starts. */
  only?: { suiteName: string; testName: string };
  /**
   * Cancels the run when aborted: checked before each suite and each test, after which nothing more starts and the
   * result carries `cancelled: true`. A `defineSandboxTest` test in flight stops at its next tick, failing with
   * `TestRunCancelledError`; a plain `it` body in flight runs to its end (it is bounded by `testTimeoutMs`).
   */
  signal?: AbortSignal;
}

/** What a `defineSandboxTest` test in flight fails with when its run is cancelled. */
export class TestRunCancelledError extends Error {
  constructor() {
    super('Test run cancelled.');
    this.name = 'TestRunCancelledError';
  }
}

/** The signal of the run in progress, if it has one — what a running `defineSandboxTest` test checks each tick. */
let activeRunSignal: AbortSignal | undefined;
export function currentRunSignal(): AbortSignal | undefined {
  return activeRunSignal;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────────────

type HookFn = () => void | Promise<void>;

interface TestCase {
  name: string;
  fn: () => void | Promise<void>;
  skip?: boolean;
  file?: string;
}

interface TestSuite {
  name: string;
  file?: string;
  tests: TestCase[];
  beforeAllFns: HookFn[];
  afterAllFns: HookFn[];
  beforeEachFns: HookFn[];
  afterEachFns: HookFn[];
}

let registeredSuites: TestSuite[] = [];
let currentSuite: TestSuite | null = null;
let currentFile: string | undefined = undefined;

/**
 * Set the verbatim file string that subsequent describe/it registrations are
 * tagged with. The engine does NOT resolve or normalize this string —
 * adapters pass whatever key they want surfaced on `TestResult.file` /
 * `SuiteResult.file`. Pass `undefined` to clear.
 */
export function setCurrentTestFile(file: string | undefined): void {
  currentFile = file;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API — imported by test files
// ─────────────────────────────────────────────────────────────────────────────

export function describe(name: string, fn: () => void): void {
  const parent = currentSuite;

  const suite: TestSuite = {
    // Nested describes get a prefixed name: "outer > inner"
    name: parent ? `${parent.name} > ${name}` : name,
    file: currentFile,
    tests: [],
    beforeAllFns: [],
    afterAllFns: [],
    // Inherit parent's per-test hooks so nested tests run them too
    beforeEachFns: parent ? [...parent.beforeEachFns] : [],
    afterEachFns: parent ? [...parent.afterEachFns] : [],
  };

  currentSuite = suite;
  fn();
  currentSuite = parent;

  // Only register suites that contain tests (avoids empty parent wrappers)
  if (suite.tests.length > 0) {
    registeredSuites.push(suite);
  }
}

describe.skip = function describeSkip(_name: string, _fn: () => void): void {
  // No-op — skip the entire suite
};

export function it(name: string, fn: () => void | Promise<void>): void {
  if (!currentSuite) {
    // Top-level it — create an implicit suite
    const suite: TestSuite = {
      name: '(root)', file: currentFile, tests: [], beforeAllFns: [], afterAllFns: [],
      beforeEachFns: [], afterEachFns: [],
    };
    suite.tests.push({ name, fn, file: currentFile });
    registeredSuites.push(suite);
    return;
  }
  currentSuite.tests.push({ name, fn, file: currentFile });
}

it.skip = function itSkip(name: string, _fn: () => void | Promise<void>): void {
  if (!currentSuite) {
    const suite: TestSuite = {
      name: '(root)', file: currentFile, tests: [], beforeAllFns: [], afterAllFns: [],
      beforeEachFns: [], afterEachFns: [],
    };
    suite.tests.push({ name, fn: _fn, skip: true, file: currentFile });
    registeredSuites.push(suite);
    return;
  }
  currentSuite.tests.push({ name, fn: _fn, skip: true, file: currentFile });
};

it.only = function itOnly(name: string, fn: () => void | Promise<void>): void {
  if (!currentSuite) return;
  // Mark this test — runCollectedTests will filter to .only tests
  (fn as any).__only = true;
  currentSuite.tests.push({ name, fn, file: currentFile });
};

export const test = it;

export function beforeEach(fn: HookFn): void {
  if (currentSuite) {
    currentSuite.beforeEachFns.push(fn);
  }
}

export function afterEach(fn: HookFn): void {
  if (currentSuite) {
    currentSuite.afterEachFns.push(fn);
  }
}

export function beforeAll(fn: HookFn): void {
  if (currentSuite) {
    currentSuite.beforeAllFns.push(fn);
  }
}

export function afterAll(fn: HookFn): void {
  if (currentSuite) {
    currentSuite.afterAllFns.push(fn);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// expect() with chainable matchers
// ─────────────────────────────────────────────────────────────────────────────

export interface Matchers {
  toBe(expected: unknown): void;
  toEqual(expected: unknown): void;
  toBeDefined(): void;
  toBeUndefined(): void;
  toBeNull(): void;
  toBeTruthy(): void;
  toBeFalsy(): void;
  toBeGreaterThan(n: number): void;
  toBeGreaterThanOrEqual(n: number): void;
  toBeLessThan(n: number): void;
  toBeLessThanOrEqual(n: number): void;
  toContain(item: unknown): void;
  toContainEqual(item: unknown): void;
  toHaveLength(length: number): void;
  toThrow(message?: string | RegExp): void;
  toBeInstanceOf(cls: Function): void;
  toMatch(pattern: string | RegExp): void;
  toHaveProperty(key: string, value?: unknown): void;
  toMatchObject(subset: object): void;
  not: Matchers;
}

class AssertionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AssertionError';
  }
}

/**
 * Surfaced as `error.name` when a spec invokes `Math.random` / `Date.now`
 * without the seeded shim. The shim installed by `installDeterministicGlobals`
 * IS the enforcement (a global override that returns deterministic values);
 * no additional per-call runtime check exists in the engine. This class is
 * exported so adapters and downstream tooling can `instanceof` / `name`-match
 * against the canonical type.
 */
export class NonDeterministicCallError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NonDeterministicCallError';
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, (b as unknown[])[i]));
  }

  const keysA = Object.keys(a as Record<string, unknown>);
  const keysB = Object.keys(b as Record<string, unknown>);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(key =>
    deepEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key],
    ),
  );
}

function formatValue(v: unknown): string {
  if (typeof v === 'string') return JSON.stringify(v);
  if (v === null) return 'null';
  if (v === undefined) return 'undefined';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function createMatchers(actual: unknown, negated: boolean): Matchers {
  const assert = (pass: boolean, message: string) => {
    const shouldPass = negated ? !pass : pass;
    if (!shouldPass) throw new AssertionError(message);
  };

  const matchers: Matchers = {
    toBe(expected: unknown) {
      assert(actual === expected,
        negated
          ? `Expected ${formatValue(actual)} not to be ${formatValue(expected)}`
          : `Expected ${formatValue(expected)} but received ${formatValue(actual)}`);
    },
    toEqual(expected: unknown) {
      assert(deepEqual(actual, expected),
        negated
          ? `Expected ${formatValue(actual)} not to deeply equal ${formatValue(expected)}`
          : `Expected ${formatValue(expected)} but received ${formatValue(actual)}`);
    },
    toBeDefined() {
      assert(actual !== undefined,
        negated ? `Expected undefined but received ${formatValue(actual)}` : `Expected value to be defined but received undefined`);
    },
    toBeUndefined() {
      assert(actual === undefined,
        negated ? `Expected value not to be undefined` : `Expected undefined but received ${formatValue(actual)}`);
    },
    toBeNull() {
      assert(actual === null,
        negated ? `Expected value not to be null` : `Expected null but received ${formatValue(actual)}`);
    },
    toBeTruthy() {
      assert(!!actual,
        negated ? `Expected ${formatValue(actual)} not to be truthy` : `Expected truthy but received ${formatValue(actual)}`);
    },
    toBeFalsy() {
      assert(!actual,
        negated ? `Expected ${formatValue(actual)} not to be falsy` : `Expected falsy but received ${formatValue(actual)}`);
    },
    toBeGreaterThan(n: number) {
      assert((actual as number) > n,
        negated ? `Expected ${actual} not to be greater than ${n}` : `Expected ${actual} to be greater than ${n}`);
    },
    toBeGreaterThanOrEqual(n: number) {
      assert((actual as number) >= n,
        negated ? `Expected ${actual} not to be >= ${n}` : `Expected ${actual} to be >= ${n}`);
    },
    toBeLessThan(n: number) {
      assert((actual as number) < n,
        negated ? `Expected ${actual} not to be less than ${n}` : `Expected ${actual} to be less than ${n}`);
    },
    toBeLessThanOrEqual(n: number) {
      assert((actual as number) <= n,
        negated ? `Expected ${actual} not to be <= ${n}` : `Expected ${actual} to be <= ${n}`);
    },
    toContain(item: unknown) {
      if (typeof actual === 'string') {
        assert((actual as string).includes(item as string),
          negated ? `Expected string not to contain ${formatValue(item)}` : `Expected string to contain ${formatValue(item)}`);
      } else if (Array.isArray(actual)) {
        assert(actual.includes(item),
          negated ? `Expected array not to contain ${formatValue(item)}` : `Expected array to contain ${formatValue(item)}`);
      } else {
        throw new AssertionError(`toContain requires a string or array, got ${typeof actual}`);
      }
    },
    // Same as toContain but uses deep equality on each element — natural
    // for arrays of objects (e.g. food list, message log) where reference
    // equality is useless. Aligns with the vitest/jest matcher of the same
    // name. AI-generated tests reach for this constantly when asserting
    // "does the list contain something shaped like X".
    toContainEqual(item: unknown) {
      if (!Array.isArray(actual)) {
        throw new AssertionError(`toContainEqual requires an array, got ${typeof actual}`);
      }
      const found = actual.some(el => deepEqual(el, item));
      assert(found,
        negated
          ? `Expected array not to contain element deeply equal to ${formatValue(item)}`
          : `Expected array to contain element deeply equal to ${formatValue(item)}`);
    },
    toHaveLength(length: number) {
      const actualLength = (actual as any)?.length;
      assert(actualLength === length,
        negated
          ? `Expected length not to be ${length} but it is`
          : `Expected length ${length} but received ${actualLength}`);
    },
    toThrow(message?: string | RegExp) {
      if (typeof actual !== 'function') {
        throw new AssertionError('toThrow requires a function');
      }
      let threw = false;
      let thrownError: unknown;
      try {
        (actual as () => void)();
      } catch (e) {
        threw = true;
        thrownError = e;
      }
      if (message === undefined) {
        assert(threw,
          negated ? `Expected function not to throw but it did` : `Expected function to throw but it didn't`);
      } else {
        const errMsg = thrownError instanceof Error ? thrownError.message : String(thrownError);
        const matches = message instanceof RegExp ? message.test(errMsg) : errMsg.includes(message);
        assert(threw && matches,
          negated
            ? `Expected function not to throw matching ${formatValue(message)}`
            : threw
              ? `Expected thrown error to match ${formatValue(message)}, got: "${errMsg}"`
              : `Expected function to throw but it didn't`);
      }
    },
    toBeInstanceOf(cls: Function) {
      assert(actual instanceof cls,
        negated
          ? `Expected ${formatValue(actual)} not to be instance of ${cls.name}`
          : `Expected instance of ${cls.name} but received ${formatValue(actual)}`);
    },
    toMatch(pattern: string | RegExp) {
      const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern);
      assert(regex.test(String(actual)),
        negated
          ? `Expected ${formatValue(actual)} not to match ${formatValue(pattern)}`
          : `Expected ${formatValue(actual)} to match ${formatValue(pattern)}`);
    },
    toHaveProperty(key: string, value?: unknown) {
      const has = actual != null && typeof actual === 'object' && key in (actual as object);
      if (value !== undefined) {
        const actualVal = has ? (actual as Record<string, unknown>)[key] : undefined;
        assert(has && deepEqual(actualVal, value),
          negated
            ? `Expected property "${key}" not to equal ${formatValue(value)}`
            : `Expected property "${key}" to equal ${formatValue(value)}, got ${formatValue(actualVal)}`);
      } else {
        assert(has,
          negated
            ? `Expected object not to have property "${key}"`
            : `Expected object to have property "${key}"`);
      }
    },
    // Subset deep-match — like toEqual but only checks keys present in
    // `subset`, recursing into nested objects/arrays. Aligns with the
    // vitest/jest matcher. AI tests reach for this when asserting a
    // captured message has a specific shape without caring about
    // extra fields the framework may add.
    toMatchObject(subset: object) {
      const matches = matchesSubset(actual, subset);
      assert(matches,
        negated
          ? `Expected object not to match subset ${formatValue(subset)}`
          : `Expected object to match subset ${formatValue(subset)}, got ${formatValue(actual)}`);
    },
    get not(): Matchers {
      return createMatchers(actual, !negated);
    },
  };
  return matchers;
}

function matchesSubset(actual: unknown, subset: unknown): boolean {
  if (subset === actual) return true;
  if (subset == null || actual == null) return false;
  if (Array.isArray(subset)) {
    if (!Array.isArray(actual) || actual.length !== subset.length) return false;
    return subset.every((s, i) => matchesSubset(actual[i], s));
  }
  if (typeof subset === 'object') {
    if (typeof actual !== 'object') return false;
    for (const key of Object.keys(subset)) {
      if (!matchesSubset((actual as Record<string, unknown>)[key], (subset as Record<string, unknown>)[key])) {
        return false;
      }
    }
    return true;
  }
  return false;
}

export function expect(actual: unknown): Matchers {
  return createMatchers(actual, false);
}

// ─────────────────────────────────────────────────────────────────────────────
// Deterministic globals (mulberry32 RNG + frozen-clock shims)
// ─────────────────────────────────────────────────────────────────────────────

interface SavedGlobals {
  mathRandom: () => number;
  dateNow: () => number;
  perfNow: (() => number) | undefined;
  /** The real process.hrtime (node), saved so restoreGlobals can put it back. */
  processHrtime: ((time?: [number, number]) => [number, number]) | undefined;
}

let savedGlobals: SavedGlobals | null = null;
let monotonicTick = 0;
/** Which install of the deterministic globals is current: each `installDeterministicGlobals` makes a new one. */
let globalsGeneration = 0;

/**
 * One install of the deterministic globals, as `installDeterministicGlobals` returns it: what a run hands back to
 * `restoreGlobals`. A later install supersedes it, and from then on restore refuses it.
 */
export interface DeterministicGlobalsToken {
  readonly generation: number;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Install seeded-RNG + frozen-clock shims.
 *
 * - `Math.random` is replaced with a mulberry32 PRNG keyed by `seed.random`.
 * - `Date.now` returns `seed.clockStart + tick`, where `tick` increments by 1
 *   on every call (deterministic monotonic). NEW Date() also reflects this
 *   (since Date's constructor with no args calls Date.now under the hood).
 * - `performance.now` mirrors the same monotonic counter (relative to install).
 *
 * The shim IS the enforcement — there is no additional per-call check. If a
 * spec invokes `Math.random` / `Date.now` while the shim is active it simply
 * gets deterministic values. `NonDeterministicCallError` exists as the named
 * class adapters surface if they install a stricter probe; the engine itself
 * doesn't throw.
 *
 * Restored by `restoreGlobals(token)`. A second install without restore — by a
 * run whose test timed out while its work went on — SUPERSEDES the first, as a
 * later virtual-clock install does: the clock restarts and the PRNG is the new
 * seed's, the real globals the first install saved stay saved, and the earlier
 * token goes stale, so that run's teardown can no longer put the real globals
 * back under the later run (it used to be a no-op: the later run ran on the
 * earlier run's seed, and lost its shim when the earlier run ended).
 */
export function installDeterministicGlobals(
  seed: { random: number; clockStart: number },
): DeterministicGlobalsToken {
  globalsGeneration += 1;
  monotonicTick = 0;
  const next = mulberry32(seed.random);
  if (savedGlobals) {
    Math.random = function deterministicRandom() {
      return next();
    };
    Date.now = function deterministicNow() {
      return seed.clockStart + monotonicTick++;
    };
    return { generation: globalsGeneration };
  }

  const savedMathRandom = Math.random;
  const savedDateNow = Date.now;
  const savedPerfNow: (() => number) | undefined =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now.bind(performance)
      : undefined;

  const proc = (globalThis as { process?: { hrtime?: unknown } }).process;
  const savedProcessHrtime =
    proc && typeof proc.hrtime === 'function'
      ? (proc.hrtime as (time?: [number, number]) => [number, number])
      : undefined;

  savedGlobals = {
    mathRandom: savedMathRandom,
    dateNow: savedDateNow,
    perfNow: savedPerfNow,
    processHrtime: savedProcessHrtime,
  };

  Math.random = function deterministicRandom() {
    return next();
  };
  Date.now = function deterministicNow() {
    return seed.clockStart + monotonicTick++;
  };
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    // Tolerant setter (shared with virtualClock's install path) — some Node
    // loader setups make `performance.now` a non-writable own property, where
    // a bare assignment throws "Cannot assign to read only property 'now'"
    // and kills runner setup outright.
    setPerformanceNow(function deterministicPerfNow() {
      return monotonicTick++;
    });
  }

  // process.hrtime PARITY (node): ServerProcess's tick gate reads
  // process.hrtime(). In the browser there is no real hrtime — the test-utils
  // nodejs mock installs a performance.now-backed shim, so under the harness
  // the gate follows the (virtualized) clock. On node the REAL hrtime would
  // survive and read real time, so the gate never opens under a virtual-clock
  // advance and server ticks silently freeze (snake movement/tick parity gap,
  // found 2026-07-23). Derive hrtime from performance.now with the SAME
  // derivation the browser mock uses — parity by construction. Read the clock
  // LAZILY at call time so later clock layers (virtual clock) are honoured.
  if (savedProcessHrtime && proc) {
    const nowMs = (): number =>
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
    const hrtimeShim = (time?: [number, number]): [number, number] => {
      const ms = nowMs();
      const seconds = Math.floor(ms / 1000);
      const nanoseconds = Math.floor((ms % 1000) * 1e6);
      if (!time) return [seconds, nanoseconds];
      let diffSeconds = seconds - time[0];
      let diffNanoseconds = nanoseconds - time[1];
      if (diffNanoseconds < 0) {
        diffSeconds -= 1;
        diffNanoseconds += 1_000_000_000;
      }
      return [diffSeconds, diffNanoseconds];
    };
    (hrtimeShim as unknown as { bigint: () => bigint }).bigint = () =>
      BigInt(Math.floor(nowMs() * 1e6));
    (proc as { hrtime: unknown }).hrtime = hrtimeShim;
  }
  return { generation: globalsGeneration };
}

/**
 * Restore the prior Math.random / Date.now / performance.now bindings. Safe to
 * call if no install happened (no-op). With `token`, only that install is
 * restored: a superseded one's is refused (returns false) — the shim is a later
 * run's now. Without a token, whatever is installed is restored. Returns whether
 * it restored.
 */
export function restoreGlobals(token?: DeterministicGlobalsToken): boolean {
  if (!savedGlobals) return false;
  if (token && token.generation !== globalsGeneration) return false;
  Math.random = savedGlobals.mathRandom;
  Date.now = savedGlobals.dateNow;
  if (savedGlobals.perfNow && typeof performance !== 'undefined') {
    setPerformanceNow(savedGlobals.perfNow);
  }
  if (savedGlobals.processHrtime) {
    const proc = (globalThis as { process?: { hrtime?: unknown } }).process;
    if (proc) (proc as { hrtime: unknown }).hrtime = savedGlobals.processHrtime;
  }
  savedGlobals = null;
  monotonicTick = 0;
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Test execution
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_TEST_TIMEOUT = 10_000;
const DEFAULT_RUN_TIMEOUT = 60_000;

class TestTimeoutError extends Error {
  constructor(testName: string, ms: number) {
    super(`Test "${testName}" timed out after ${ms}ms`);
    this.name = 'TestTimeoutError';
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  if (ms <= 0) return promise;
  // The per-test timeout MUST run on the REAL wall clock, even when the virtual
  // clock is active (real-runtime path): the virtual clock only advances when the
  // test driver ticks it, so a hung test would never advance its own timeout. When
  // virtual timers aren't installed these are just the live globals → identical
  // behaviour for the default harness path.
  const realSetTimeout = getRealSetTimeout();
  const realClearTimeout = getRealClearTimeout();
  return new Promise<T>((resolve, reject) => {
    const timer = realSetTimeout(() => reject(new TestTimeoutError(label, ms)), ms);
    promise.then(
      v => { realClearTimeout(timer); resolve(v); },
      e => { realClearTimeout(timer); reject(e); },
    );
  });
}

/**
 * Maximum number of stack frames retained on `TestErrorInfo.errorFrames`. The
 * top of the stack is almost always the actually-thrown call-site; deeper
 * frames are framework plumbing the fix-loop doesn't need. Keeping the payload
 * bounded also keeps the JSON-compared `TestResult` small.
 */
const MAX_ERROR_FRAMES = 5;

/**
 * Substrings that disqualify a `/`-prefixed frame from being treated as a
 * virtual user-code call-site. The bare leading-slash filter alone is enough
 * in the BROWSER (where there is no Unix filesystem and the only `/`-prefixed
 * frames come from the `//# sourceURL` directive), but in NODE every absolute
 * filesystem path also starts with `/` — so engine-internal frames
 * (testRuntime.ts, NodeRunnerAdapter.ts, vitest workers, jsdom, etc.) would
 * leak through and break byte-identical parity between the two runners.
 *
 * These segments are matched as raw substrings of the captured path. Anything
 * matching is dropped before the cap is applied. The list is intentionally
 * narrow — only well-known internal locations — so genuine user/virtual
 * frames are never mistakenly filtered.
 */
const NON_VIRTUAL_FRAME_BLOCKLIST: readonly string[] = [
  '/node_modules/',
  'node:internal',
  '/packages/sandbox-test/',
  '/packages/sandbox-core/',
  '/packages/sandbox-ai-v2/',
  '/packages/sandbox-backend/',
  // The framework a test boots its game through (the playground mount, the
  // client/server processes, core, test-utils' environment). In Node these are
  // absolute workspace paths (src under tsx, dist through the workspace
  // symlink); in the browser they are http(s) URLs that never qualify. Left
  // out, a framework console call during a test — the playground's boot log —
  // is captured as game output on the node side only.
  '/packages/playground/',
  '/packages/client/',
  '/packages/server/',
  '/packages/core/',
  '/packages/test-utils/',
  // Test-host drivers (e.g. the parity harness's node-runner) — absolute
  // host paths that would otherwise leak into errorFrames on the node side
  // only and break browser↔node parity.
  '/apps/sandbox-e2e/',
];

/**
 * Extract virtual-path call-sites from a V8 `.stack` string.
 *
 * Both Node V8 and Chromium V8 produce two frame shapes interchangeably:
 *   `    at <fn> (path:line:col)`
 *   `    at path:line:col`
 *
 * The transpiler appends `//# sourceURL=${entryFile}` to every compiled module,
 * so frames that originated in user/virtual code carry a path beginning with
 * `/` (e.g. `/virtual/snake/SnakeServerSystem.ts:42:7`). Frames from
 * `<anonymous>`, `node_modules`, jsdom internals, etc. do NOT match the
 * leading-slash pattern and are dropped. In Node we additionally drop frames
 * whose path contains a `NON_VIRTUAL_FRAME_BLOCKLIST` segment — those are
 * absolute filesystem paths into engine/test-host code that vary between
 * runners and would break parity.
 *
 * Output frames are normalised to the literal string `path:line:col` so the
 * parsed array is byte-identical between Browser V8 and Node V8.
 *
 * Returns `undefined` for a missing / empty stack (or one with no surviving
 * virtual frames) so the field can be omitted from the serialised TestResult
 * entirely when there is nothing to surface.
 */
export function parseErrorFrames(stack: string | undefined | null): string[] | undefined {
  if (!stack) return undefined;
  const re = /(?:\bat\s+(?:[^(]+\s+\()?)(\/[\S]+\.tsx?:\d+:\d+)/g;
  const frames: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(stack)) !== null) {
    const path = m[1];
    if (NON_VIRTUAL_FRAME_BLOCKLIST.some(seg => path.includes(seg))) continue;
    frames.push(path);
    if (frames.length >= MAX_ERROR_FRAMES) break;
  }
  return frames.length > 0 ? frames : undefined;
}

function toErrorInfo(err: unknown, fallbackName = 'Error'): TestErrorInfo {
  if (err instanceof Error) {
    const info: TestErrorInfo = { name: err.name || fallbackName, message: err.message };
    const frames = parseErrorFrames(err.stack);
    if (frames) info.errorFrames = frames;
    return info;
  }
  return { name: fallbackName, message: String(err) };
}

const CAPTURE_LOG_LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;
const MAX_CAPTURED_LOGS = 30;

export function formatLogArgs(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return a.message;
      try { return JSON.stringify(a); } catch { return String(a); }
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .slice(0, 200);
}

/**
 * True when a `console.error` message is the FRAMEWORK's own "I caught a
 * throw from your system" report — `@gotti/server`'s `ServerProcess.ts` /
 * `@gotti/client`'s `ClientProcess.ts` catch blocks around a system's
 * per-tick `update()` (`'[gotti] error in system update — game loop
 * continues'`, swallowed — the loop keeps going) and `onClientJoined`
 * (`'Error in system <name> onClientJoined for client <id>:'`, then
 * rethrown). Both fire from REAL, non-virtual framework files (dist `.js`,
 * not a `//# sourceURL`-tagged `.ts` virtual module), so `defaultLogSite()`
 * can never attribute them to a call-site — without this exception they are
 * silently dropped by the "can't attribute → drop" rule below, hiding the
 * exact reason a generated system crashed (e.g. `this.getAllSnakes is not a
 * function`) from a test that only sees an unrelated wrong-value assertion.
 * Deliberately narrow: matches ONLY these two known framework catch-site
 * message shapes, never general framework chatter.
 */
export function isCaughtSystemErrorLog(message: string): boolean {
  return message.startsWith('Error in system ') || message.startsWith('[gotti] error in system update');
}

/**
 * Default call-site resolver: the first VIRTUAL, non-spec stack frame (a
 * generated system file) from the live `new Error().stack`. Returns undefined
 * for framework/spec frames — those are dropped so we only capture what the
 * SYSTEM under test logged. (Injectable in installLogCapture for testing.)
 */
export function defaultLogSite(): string | undefined {
  try {
    const frames = parseErrorFrames(new Error().stack);
    return frames?.find((f) => !/\.(spec|test)\.[tj]sx?:/.test(f));
  } catch {
    return undefined;
  }
}

/**
 * Wrap the console for ONE test so each GENERATED-CODE console call is captured
 * with its source call-site and a count. Returns finish() which restores the
 * console and yields the deduped logs (most-frequent first). `getSite` resolves
 * the call-site (default: the stack); a call with no site is skipped.
 */
export function installLogCapture(
  getSite: () => string | undefined = defaultLogSite,
): () => CapturedLog[] {
  const original: Record<string, ((...a: unknown[]) => void) | undefined> = {};
  const agg = new Map<string, CapturedLog>();
  const order: string[] = [];
  const c = console as unknown as Record<string, ((...a: unknown[]) => void) | undefined>;
  for (const level of CAPTURE_LOG_LEVELS) {
    const orig = c[level]; // keep the EXACT original reference for restore
    original[level] = orig;
    c[level] = (...args: unknown[]) => {
      if (typeof orig === 'function') orig.apply(console, args); // preserve normal output
      const message = formatLogArgs(args);
      const site = getSite();
      const isCaughtSystemError = level === 'error' && isCaughtSystemErrorLog(message);
      // Normally drop logs we can't attribute to generated code (avoids capturing
      // the engine's own console). EXCEPTIONS: explicit `[OBSERVE]` instrumentation
      // must survive even when the call fires from a timer-driven simulation frame
      // whose virtual path the stack parser can't resolve (the whole point of the
      // fix loop's observe-before-fix pass); and the framework's own caught-system-
      // error reports (see `isCaughtSystemErrorLog`) — the single most useful debug
      // signal when a generated system crashes, and otherwise invisible.
      if (!site && !message.startsWith('[OBSERVE]') && !isCaughtSystemError) return;
      const resolvedSite = site ?? (isCaughtSystemError ? '<framework>' : '<runtime>');
      const key = `${level}|${resolvedSite}|${message}`;
      const ex = agg.get(key);
      if (ex) { ex.count += 1; return; }
      if (agg.size >= MAX_CAPTURED_LOGS) return;
      const entry: CapturedLog = { level, site: resolvedSite, message, count: 1 };
      agg.set(key, entry);
      order.push(key);
    };
  }
  return () => {
    for (const level of CAPTURE_LOG_LEVELS) c[level] = original[level];
    return order.map((k) => agg.get(k)!).sort((a, b) => b.count - a.count);
  };
}

/**
 * Returned alongside `TestRunResult` by `runCollectedTestsWithMetadata`. Kept
 * OUT of the comparable result so coordinator parity checks aren't broken by
 * intrinsically non-deterministic wall-clock numbers.
 */
export interface TestRunResultWithMetadata {
  result: TestRunResult;
  metadata: RunMetadata;
}

/**
 * Same as `runCollectedTests` but additionally returns the wall-clock
 * durations as a separate `RunMetadata` object. Adapters can keep the
 * durations for diagnostics while passing only `TestRunResult` to the
 * coordinator for byte-identical parity comparison.
 */
export async function runCollectedTestsWithMetadata(
  options?: RunTestsOptions,
): Promise<TestRunResultWithMetadata> {
  return runCollectedTestsImpl(options);
}

/**
 * Run all collected test suites and return structured results.
 * Resets registrations after running.
 */
export async function runCollectedTests(options?: RunTestsOptions): Promise<TestRunResult> {
  const { result } = await runCollectedTestsImpl(options);
  return result;
}

async function runCollectedTestsImpl(
  options?: RunTestsOptions,
): Promise<TestRunResultWithMetadata> {
  const suites = registeredSuites;
  registeredSuites = [];
  currentSuite = null;

  const testTimeoutMs = options?.testTimeoutMs ?? DEFAULT_TEST_TIMEOUT;
  const runTimeoutMs = options?.runTimeoutMs ?? DEFAULT_RUN_TIMEOUT;
  const onProgress = options?.onProgress;
  const only = options?.only;
  const signal = options?.signal;
  const previousRunSignal = activeRunSignal;
  activeRunSignal = signal;
  let cancelled = false;

  // ── Host-crash isolation (snake e2e take27 follow-up) ─────────────────
  // LLM-generated game systems can throw inside their async update loops
  // (e.g. SnakeServerSystem.onUpdate dereferencing `snake.body[0]` when
  // the snake has been collision-killed mid-tick). Those throws escape
  // through `setImmediate(this.gameLoop)` in @gotti/server's ServerProcess
  // and Node's default uncaughtException handler terminates the host —
  // which kills the entire eval mid-run (take27 trace: exit at sub-agent
  // iteration 4, no FINAL STATE emitted, no metrics captured).
  //
  // Test runs are an unsuitable context for fail-fast: we WANT to keep
  // running so the test assertion catches the bad behaviour. Install a
  // scoped uncaughtException + unhandledRejection handler for the
  // lifetime of this test run that logs the error, marks the currently-
  // executing test as failed (when one is in flight), and lets the run
  // continue. Restored at the end via finally so production runtime is
  // untouched.
  let runtimeCrashError: TestErrorInfo | undefined;
  let currentTestRef: { suite: string; name: string } | undefined;
  const onHostCrash = (err: unknown): void => {
    const info = toErrorInfo(err, 'HostCrashError');
    // First crash wins — subsequent crashes from the same broken loop
    // get logged but not re-recorded (the FIRST one is what tripped
    // the test).
    if (!runtimeCrashError) {
      runtimeCrashError = info;
    }
    const tag = currentTestRef
      ? `[testRuntime host-crash isolated during "${currentTestRef.suite} > ${currentTestRef.name}"]`
      : `[testRuntime host-crash isolated between tests]`;
    // eslint-disable-next-line no-console
    console.error(`${tag} ${info.message}`);
  };
  // ── Node vs browser ──────────────────────────────────────────────────
  // The host-crash isolation here only makes sense in Node — `process` is
  // the Node global. In the browser sandbox (SandboxRunnerProvider) there
  // is no `process`, so even *referencing* it throws ReferenceError. That
  // bubbles up to `executeTests`, which catches it and synthesises a
  // "load error" suite for EVERY test file — exactly the symptom we saw
  // in the v2 snake eval (6 tests, all "load error", no underlying message
  // for the planner to act on). Skip the whole handler dance when process
  // isn't a Node-shaped object.
  const hasNodeProcess =
    typeof process !== 'undefined' && typeof (process as { on?: unknown }).on === 'function';
  const priorUncaught = hasNodeProcess ? process.listeners('uncaughtException').slice() : [];
  const priorUnhandled = hasNodeProcess ? process.listeners('unhandledRejection').slice() : [];
  if (hasNodeProcess) {
    for (const l of priorUncaught) process.off('uncaughtException', l);
    for (const l of priorUnhandled) process.off('unhandledRejection', l);
    process.on('uncaughtException', onHostCrash);
    process.on('unhandledRejection', onHostCrash);
  }
  const restoreHostHandlers = (): void => {
    if (!hasNodeProcess) return;
    process.off('uncaughtException', onHostCrash);
    process.off('unhandledRejection', onHostCrash);
    for (const l of priorUncaught) process.on('uncaughtException', l as never);
    for (const l of priorUnhandled) process.on('unhandledRejection', l as never);
  };

  // Per-test wall-clock durations (kept out of the comparable result; reported
  // via the separate RunMetadata).
  const perTestDurations: RunMetadata['perTestDurations'] = [];

  try {

  // Handle it.only — if ANY test in ANY suite has __only, filter everything else
  const hasOnly = suites.some(s => s.tests.some(t => (t.fn as any).__only));
  // …and the run's own `only`: one named test of one named suite.
  const selected = (suiteName: string, t: { name: string; fn: unknown }): boolean =>
    (!hasOnly || !!(t.fn as any).__only) && (!only || (suiteName === only.suiteName && t.name === only.testName));

  const suiteResults: SuiteResult[] = [];
  const totalStart = performance.now();
  let totalTests = 0;
  let totalPassed = 0;
  let totalFailed = 0;
  let totalSkipped = 0;
  let runTimedOut = false;

  // Count tests for run-start event
  const countableTests = suites.reduce((n, s) => n + s.tests.filter(t => selected(s.name, t)).length, 0);

  onProgress?.({ type: 'run-start', totalSuites: suites.length, totalTests: countableTests });

  for (const suite of suites) {
    if (runTimedOut) break;
    if (signal?.aborted) { cancelled = true; break; }

    const testResults: TestResult[] = [];
    let suitePassed = 0;
    let suiteFailed = 0;
    let suiteSkipped = 0;

    // Determine which tests to actually run
    const testsToRun = suite.tests.filter(t => selected(suite.name, t));

    if (testsToRun.length === 0) continue;

    onProgress?.({ type: 'suite-start', suiteName: suite.name, file: suite.file, testCount: testsToRun.length });

    // ── beforeAll ──
    let beforeAllFailed = false;
    for (const fn of suite.beforeAllFns) {
      try {
        await fn();
      } catch (err) {
        beforeAllFailed = true;
        const info = toErrorInfo(err);
        // All tests in this suite get marked as errors
        for (const tc of testsToRun) {
          totalTests++;
          totalFailed++;
          suiteFailed++;
          const tr: TestResult = {
            name: tc.name, suiteName: suite.name, file: tc.file,
            status: 'error',
            error: {
              name: info.name,
              message: `beforeAll failed: ${info.message}`,
              ...(info.errorFrames ? { errorFrames: info.errorFrames } : {}),
            },
          };
          testResults.push(tr);
          perTestDurations.push({ suiteName: suite.name, testName: tc.name, durationMs: 0 });
          onProgress?.({ type: 'test-end', suiteName: suite.name, testName: tc.name, file: tc.file, status: 'error', error: tr.error });
        }
        break;
      }
    }

    if (!beforeAllFailed) {
      for (const tc of testsToRun) {
        if (signal?.aborted) { cancelled = true; break; }
        // Check overall run timeout
        if (runTimeoutMs > 0 && performance.now() - totalStart > runTimeoutMs) {
          runTimedOut = true;
          totalTests++;
          totalFailed++;
          suiteFailed++;
          const tr: TestResult = {
            name: tc.name, suiteName: suite.name, file: tc.file,
            status: 'error',
            error: { name: 'RunTimeoutError', message: `Overall run timeout exceeded (${runTimeoutMs}ms)` },
          };
          testResults.push(tr);
          perTestDurations.push({ suiteName: suite.name, testName: tc.name, durationMs: 0 });
          onProgress?.({ type: 'test-end', suiteName: suite.name, testName: tc.name, file: tc.file, status: 'error', error: tr.error });
          break;
        }

        totalTests++;

        if (tc.skip) {
          totalSkipped++;
          suiteSkipped++;
          const tr: TestResult = { name: tc.name, suiteName: suite.name, file: tc.file, status: 'skipped' };
          testResults.push(tr);
          perTestDurations.push({ suiteName: suite.name, testName: tc.name, durationMs: 0 });
          onProgress?.({ type: 'test-end', suiteName: suite.name, testName: tc.name, file: tc.file, status: 'skipped' });
          continue;
        }

        onProgress?.({ type: 'test-start', suiteName: suite.name, testName: tc.name, file: tc.file });

        // Tag the test ref BEFORE running so async host-crash handlers can
        // attribute escaping errors (e.g. ServerProcess.gameLoop throws on
        // a subsequent setImmediate tick) to the right test in the logs.
        currentTestRef = { suite: suite.name, name: tc.name };

        const testStart = performance.now();
        let status: TestStatus = 'passed';
        let errorInfo: TestErrorInfo | undefined;
        // Capture what the generated system code logs during THIS test.
        const finishLogs = installLogCapture();
        let capturedLogs: CapturedLog[] = [];

        try {
          for (const fn of suite.beforeEachFns) await fn();

          const result = tc.fn();
          if (result && typeof (result as any).then === 'function') {
            await withTimeout(result as Promise<void>, testTimeoutMs, tc.name);
          }

          suitePassed++;
          totalPassed++;
        } catch (err) {
          status = err instanceof AssertionError ? 'failed' : 'error';
          errorInfo = toErrorInfo(err);
          suiteFailed++;
          totalFailed++;
        }

        // If a host-crash happened during this test (caught by the scoped
        // uncaughtException handler installed above), promote it to a test
        // failure when the test otherwise passed. Don't overwrite a real
        // assertion failure — that one was the test's primary observation.
        if (runtimeCrashError && status === 'passed') {
          status = 'error';
          errorInfo = {
            name: runtimeCrashError.name,
            message: `Host-crash escaped to timer queue (game-loop unhandled exception): ${runtimeCrashError.message}`,
            ...(runtimeCrashError.errorFrames ? { errorFrames: runtimeCrashError.errorFrames } : {}),
          };
          suitePassed--;
          totalPassed--;
          suiteFailed++;
          totalFailed++;
        }
        // Clear so the NEXT test starts fresh; the handler attribution
        // window is one test at a time.
        runtimeCrashError = undefined;

        // Always run afterEach, even if the test failed
        try {
          for (const fn of suite.afterEachFns) await fn();
        } catch (afterErr) {
          // If test passed but afterEach fails, mark as error
          if (status === 'passed') {
            const info = toErrorInfo(afterErr);
            status = 'error';
            errorInfo = {
              name: info.name,
              message: `afterEach failed: ${info.message}`,
              ...(info.errorFrames ? { errorFrames: info.errorFrames } : {}),
            };
            suitePassed--;
            totalPassed--;
            suiteFailed++;
            totalFailed++;
          }
        }

        capturedLogs = finishLogs();
        const durationMs = performance.now() - testStart;
        perTestDurations.push({ suiteName: suite.name, testName: tc.name, durationMs });
        testResults.push({
          name: tc.name, suiteName: suite.name, file: tc.file, status, error: errorInfo,
          ...(capturedLogs.length > 0 ? { logs: capturedLogs } : {}),
        });
        onProgress?.({ type: 'test-end', suiteName: suite.name, testName: tc.name, file: tc.file, status, error: errorInfo });
      }
    }

    // ── afterAll — always runs (even if beforeAll succeeded but tests failed) ──
    if (!beforeAllFailed) {
      for (const fn of suite.afterAllFns) {
        try { await fn(); } catch { /* swallow — tests already recorded */ }
      }
    }

    const suiteResult: SuiteResult = {
      name: suite.name,
      file: suite.file,
      tests: testResults,
      passed: suitePassed,
      failed: suiteFailed,
      skipped: suiteSkipped,
    };
    suiteResults.push(suiteResult);
    onProgress?.({ type: 'suite-end', suite: suiteResult });
  }

  const runResult: TestRunResult = {
    suites: suiteResults,
    totalTests,
    totalPassed,
    totalFailed,
    totalSkipped,
    ...(cancelled ? { cancelled: true as const } : {}),
  };
  const metadata: RunMetadata = {
    durationMs: performance.now() - totalStart,
    perTestDurations,
  };
  onProgress?.({ type: 'run-end', result: runResult });

  return { result: runResult, metadata };
  } finally {
    // Always restore the prior uncaughtException / unhandledRejection
    // handlers so production runtime / host's fail-fast behaviour is
    // untouched outside the test-run window. Voided-reference cleanup
    // safe to run even if the try block threw partway through.
    restoreHostHandlers();
    activeRunSignal = previousRunSignal;
  }
}

/**
 * Reset all registered suites without running them.
 */
export function resetTests(): void {
  registeredSuites = [];
  currentSuite = null;
  currentFile = undefined;
}

/**
 * Get the count of registered (but not yet run) test suites.
 */
export function getRegisteredSuiteCount(): number {
  return registeredSuites.length;
}

export interface CollectedSuite {
  name: string;
  file?: string;
  tests: { name: string; skip?: boolean; file?: string }[];
}

/**
 * Read the suites/tests registered by the most recent test-file load WITHOUT
 * running them. Loading a test file evaluates its top-level `describe`/`it`
 * (and `defineSandboxTest`, which lowers to `describe`/`it`) calls, which only
 * REGISTER names — the test bodies don't run until `runCollectedTests()`. So a
 * caller can `resetTests()` → load a file → `collectRegisteredTests()` to get
 * the exact suite + test names for a pre-run preview. This is accurate where
 * source-regex scanning is not: it sees the real registered labels even when
 * tests are produced by a helper/factory (e.g. `defineSandboxTest`'s
 * `tests: [movementTest(...)]`), which a regex can't statically extract.
 */
export function collectRegisteredTests(): CollectedSuite[] {
  return registeredSuites.map(s => ({
    name: s.name,
    file: s.file,
    tests: s.tests.map(t => ({ name: t.name, skip: t.skip, file: t.file })),
  }));
}
